import getMediaElementsByUuids from 'editor/src/store/design/selector/getMediaElementsByUuids';
import { MediaElement } from 'editor/src/store/design/types';
import { areAllTextElements } from 'editor/src/store/design/util/elementTypeGuards';
import getPreviewModePluginName from 'editor/src/store/editor/selector/getPreviewModePluginName';
import isAnyMediaElementInCropMode from 'editor/src/store/editor/selector/isAnyMediaElementInCropMode';
import { ManipulationMode } from 'editor/src/store/editor/types';
import { useSelector } from 'editor/src/store/hooks';

import useFilteredMenuItems from 'editor/src/component/Menu/useFilteredMenuItems';

import {
  ADDON_SELECTED_ELTS,
  CROP_ELTS,
  DEFAULT_ELTS,
  GRID_SELECTED_ELTS,
  IMAGE_SELECTED_ELTS,
  LINE_SELECTED_ELTS,
  MULTIPLE_SELECTED_TEXT_ELTS,
  PERSPECTIVE_TRANSFORM_ELTS,
  PLUGIN_PREVIEW_MODE_ELTS,
  RECTANGLE_SELECTED_ELTS,
  TEXT_SELECTED_ELTS,
} from './menuItems';

function getMenuItems(
  inCropMode: boolean,
  isPerspectiveTransformMode: boolean,
  inPluginPreviewMode: boolean,
  elements: MediaElement[] | undefined,
) {
  if (inCropMode) {
    return CROP_ELTS;
  }
  if (isPerspectiveTransformMode) {
    return PERSPECTIVE_TRANSFORM_ELTS;
  }
  if (inPluginPreviewMode) {
    return PLUGIN_PREVIEW_MODE_ELTS;
  }

  // Handle no elements case
  if (!elements?.length || !elements[0]?.type) {
    return DEFAULT_ELTS;
  }

  // Handle multiple elements case
  if (elements.length > 1) {
    if (areAllTextElements(elements)) {
      return MULTIPLE_SELECTED_TEXT_ELTS;
    }
    return DEFAULT_ELTS;
  }

  // Handle single element case
  const firstElement = elements[0];
  switch (firstElement.type) {
    case 'text':
      return TEXT_SELECTED_ELTS;
    case 'addon':
      return ADDON_SELECTED_ELTS;
    case 'image':
      return IMAGE_SELECTED_ELTS;
    case 'grid':
      return GRID_SELECTED_ELTS;
    case 'line':
      return LINE_SELECTED_ELTS;
    case 'rectangle':
      return RECTANGLE_SELECTED_ELTS;
    default:
      return DEFAULT_ELTS;
  }
}

function useMenuItems() {
  const activeElements = useSelector((state) =>
    state.editor.selectedElementUuids.length ? getMediaElementsByUuids(state, state.editor.selectedElementUuids) : [],
  );

  const firstElement = activeElements.length === 1 ? activeElements[0] : undefined;

  const inCropMode = useSelector(isAnyMediaElementInCropMode);
  const isPerspectiveTransformMode = useSelector(
    (state) => state.editor.elementManipulationMode?.mode === ManipulationMode.perspectiveTransform,
  );
  const isPluginPreviewMode = useSelector((state) =>
    firstElement ? !!getPreviewModePluginName(state, firstElement?.uuid) : false,
  );

  const items = getMenuItems(inCropMode, isPerspectiveTransformMode, isPluginPreviewMode, activeElements);
  return useFilteredMenuItems(items);
}

export default useMenuItems;
